/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Volume2, Play, Pause, SkipForward, SkipBack } from 'lucide-react';
import { audioEngine, IntervalCalculator } from '../../audio/AudioEngine';
import { getIntervalColor } from '../../config/colorScheme';
import { getIntervalReferences, getRandomReference } from '../../data/intervalReferences';

interface EarTrainingLearnProps {
  currentKey: string;
  currentScale: string;
  noteCollection: any;
}

interface LearningInterval {
  interval: string;
  name: string;
  rootNote: string;
  intervalNote: string;
  examples: { root: string; interval: string; context: string }[];
}

const EarTrainingLearn: React.FC<EarTrainingLearnProps> = ({
  currentKey,
  currentScale,
  noteCollection
}) => {
  const [selectedInterval, setSelectedInterval] = useState<string>('P5');
  const [playbackType, setPlaybackType] = useState<'melodic' | 'harmonic'>('melodic');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentExample, setCurrentExample] = useState(0);

  // Initialize audio on component mount
  useEffect(() => {
    audioEngine.initialize().catch(console.error);
  }, []);

  // Learning progression - ordered by difficulty
  const learningProgression = [
    { interval: 'P8', difficulty: 1, description: 'Same note, higher octave' },
    { interval: 'P5', difficulty: 2, description: 'Perfect 5th - most consonant' },
    { interval: 'P4', difficulty: 2, description: 'Perfect 4th - very stable' },
    { interval: 'M3', difficulty: 3, description: 'Major 3rd - happy sound' },
    { interval: 'm3', difficulty: 3, description: 'Minor 3rd - sad sound' },
    { interval: 'M2', difficulty: 4, description: 'Major 2nd - step up' },
    { interval: 'm2', difficulty: 5, description: 'Minor 2nd - half step, tense' },
    { interval: 'M6', difficulty: 4, description: 'Major 6th - bright, open' },
    { interval: 'm6', difficulty: 4, description: 'Minor 6th - darker' },
    { interval: 'M7', difficulty: 5, description: 'Major 7th - very tense' },
    { interval: 'm7', difficulty: 5, description: 'Minor 7th - bluesy' },
    { interval: 'TRI', difficulty: 6, description: 'Tritone - devil\'s interval' }
  ];

  // Generate examples for the current interval
  const generateExamples = (interval: string): LearningInterval => {
    const examples = [
      { root: `${currentKey}4`, interval: IntervalCalculator.calculateIntervalNote(`${currentKey}4`, interval), context: `In ${currentKey} ${currentScale}` },
      { root: 'C4', interval: IntervalCalculator.calculateIntervalNote('C4', interval), context: 'In C Major' },
      { root: 'G4', interval: IntervalCalculator.calculateIntervalNote('G4', interval), context: 'In G Major' },
      { root: 'F4', interval: IntervalCalculator.calculateIntervalNote('F4', interval), context: 'In F Major' }
    ];

    return {
      interval,
      name: IntervalCalculator.getIntervalName(interval),
      rootNote: examples[currentExample].root,
      intervalNote: examples[currentExample].interval,
      examples
    };
  };

  const currentLearning = generateExamples(selectedInterval);

  // Play the current interval
  const playCurrentInterval = async () => {
    if (isPlaying) return;
    
    setIsPlaying(true);
    try {
      await audioEngine.playInterval(
        currentLearning.rootNote,
        currentLearning.intervalNote,
        playbackType,
        { duration: 1500 }
      );
    } catch (error) {
      console.error('Audio playback error:', error);
    } finally {
      setIsPlaying(false);
    }
  };

  // Play just the root note
  const playRootNote = async () => {
    try {
      await audioEngine.playNote(currentLearning.rootNote, { duration: 800 });
    } catch (error) {
      console.error('Audio playback error:', error);
    }
  };

  // Play just the interval note
  const playIntervalNote = async () => {
    try {
      await audioEngine.playNote(currentLearning.intervalNote, { duration: 800 });
    } catch (error) {
      console.error('Audio playback error:', error);
    }
  };

  // Navigate examples
  const nextExample = () => {
    setCurrentExample((prev) => (prev + 1) % currentLearning.examples.length);
  };

  const prevExample = () => {
    setCurrentExample((prev) => prev === 0 ? currentLearning.examples.length - 1 : prev - 1);
  };

  // Get interval info
  const intervalInfo = learningProgression.find(i => i.interval === selectedInterval);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Volume2 className="h-5 w-5" />
          Learn Intervals
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Interval Selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Select Interval to Learn</label>
          <Select value={selectedInterval} onValueChange={setSelectedInterval}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {learningProgression.map((item) => (
                <SelectItem key={item.interval} value={item.interval}>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getIntervalColor(item.interval) }}
                    />
                    {IntervalCalculator.getIntervalName(item.interval)}
                    <Badge variant="secondary" className="text-xs">
                      Level {item.difficulty}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Current Interval Info */}
        <Card className="p-4 bg-muted/30">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: getIntervalColor(selectedInterval) }}
              >
                {selectedInterval}
              </div>
              <h3 className="font-semibold">{currentLearning.name}</h3>
            </div>
            {intervalInfo && (
              <p className="text-sm text-muted-foreground">{intervalInfo.description}</p>
            )}
          </div>
        </Card>


        {/* Current Example */}
        <Card className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Example {currentExample + 1} of {currentLearning.examples.length}</h4>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={prevExample}>
                  <SkipBack className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={nextExample}>
                  <SkipForward className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">{currentLearning.examples[currentExample].context}</p>
              <div className="flex items-center justify-center gap-2 text-lg">
                <span className="font-mono bg-muted px-2 py-1 rounded">
                  {currentLearning.rootNote}
                </span>
                <span>→</span>
                <span className="font-mono bg-muted px-2 py-1 rounded">
                  {currentLearning.intervalNote}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Playback Controls */}
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Playback Type</label>
            <Select value={playbackType} onValueChange={(value: any) => setPlaybackType(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="melodic">Melodic (one after another)</SelectItem>
                <SelectItem value="harmonic">Harmonic (together)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Play Buttons */}
          <div className="grid grid-cols-3 gap-2">
            <Button 
              variant="outline" 
              onClick={playRootNote}
              className="flex-1"
            >
              Root Note
            </Button>
            <Button 
              onClick={playCurrentInterval}
              disabled={isPlaying}
              className="flex-1"
            >
              {isPlaying ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
              Play Interval
            </Button>
            <Button 
              variant="outline" 
              onClick={playIntervalNote}
              className="flex-1"
            >
              Interval Note
            </Button>
          </div>
        </div>

        {/* Learning Tips with Song References */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">💡 Learning Tips & Song References</h4>
          <div className="space-y-3">
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Listen to each interval multiple times before moving on</li>
              <li>• Try both melodic and harmonic playback</li>
              <li>• Pay attention to how the interval feels emotionally</li>
              <li>• Practice in different keys to generalize the sound</li>
            </ul>
            
            {(() => {
              const references = getIntervalReferences(selectedInterval);
              if (!references || references.examples.length === 0) return null;
              
              return (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-sm font-medium text-blue-900 mb-2">🎵 Remember this interval by thinking of:</p>
                  <div className="space-y-1">
                    {references.examples.map((example, index) => (
                      <div key={index} className="text-sm text-blue-800">
                        <span className="font-medium">• {example.commonName}</span>
                        {example.memoryTip && (
                          <span className="italic text-blue-700"> - {example.memoryTip}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-700 mt-2 italic">
                    💡 Sing or hum these melodies to internalize the interval sound!
                  </p>
                </div>
              );
            })()}
          </div>
        </Card>
      </CardContent>
    </Card>
  );
};

export default EarTrainingLearn;